import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { bookingAPI, tutorAPI, zoomAPI, messageAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  Calendar, 
  Clock, 
  Video,
  MessageSquare,
  DollarSign,
  Users,
  Loader2,
  LogOut,
  Settings,
  CheckCircle,
  XCircle,
  AlertTriangle,
  User,
  BookOpen,
  Award,
  Globe,
  MapPin,
  Play,
  Camera,
  Save
} from 'lucide-react';

const SUBJECTS = [
  'Mathematics', 'Physics', 'Chemistry', 'Biology', 'Calculus',
  'Linear Algebra', 'Statistics', 'Computer Science', 'Programming',
  'Data Science', 'English', 'Writing', 'Economics', 'Accounting'
];

const GRADE_LEVELS = [
  { id: 'high_school', name: 'High School' },
  { id: 'university', name: 'University/College' },
  { id: 'graduate', name: 'Graduate Level' }
];

const LANGUAGES = [
  'English', 'French', 'Spanish', 'Mandarin', 'Hindi', 'Arabic', 
  'Portuguese', 'German', 'Japanese', 'Korean', 'Italian', 'Russian'
];

const TIME_SLOTS = [
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00',
  '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00'
];

const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// Profile Editor Component
const ProfileEditor = ({ profile, onSave, saving }) => {
  const [formData, setFormData] = useState({
    subjects: profile?.subjects || [],
    grade_levels: profile?.grade_levels || [],
    hourly_rate: profile?.hourly_rate || 0,
    bio: profile?.bio || '',
    qualifications: profile?.qualifications || '',
    educational_background: profile?.educational_background || '',
    teaching_style: profile?.teaching_style || '',
    experience_years: profile?.experience_years || 0,
    languages: profile?.languages || ['English'],
    location: profile?.location || 'Online',
    intro_video_url: profile?.intro_video_url || '',
    profile_photo_url: profile?.profile_photo_url || '',
    availability: profile?.availability || {}
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  const toggleTimeSlot = (day, slot) => {
    setFormData(prev => {
      const daySlots = prev.availability[day] || [];
      const newSlots = daySlots.includes(slot)
        ? daySlots.filter(s => s !== slot)
        : [...daySlots, slot].sort();
      
      return {
        ...prev,
        availability: {
          ...prev.availability,
          [day]: newSlots
        }
      };
    });
  };

  const handleSubmit = () => {
    // Clean up empty availability days
    const cleanedAvailability = {};
    Object.entries(formData.availability).forEach(([day, slots]) => {
      if (slots && slots.length > 0) {
        cleanedAvailability[day] = slots;
      }
    });
    
    onSave({ ...formData, availability: cleanedAvailability });
  };

  return (
    <div className="space-y-8">
      {/* Basic Information */}
      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <User className="h-5 w-5 text-indigo-700" />
            Basic Information
          </CardTitle>
          <CardDescription>Your public profile information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Hourly Rate ($)</Label>
              <Input
                type="number"
                min="0"
                step="5"
                value={formData.hourly_rate}
                onChange={(e) => updateField('hourly_rate', parseFloat(e.target.value) || 0)}
                data-testid="hourly-rate-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Years of Teaching Experience</Label>
              <Input
                type="number"
                min="0"
                value={formData.experience_years}
                onChange={(e) => updateField('experience_years', parseInt(e.target.value) || 0)}
                data-testid="experience-years-input"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Location</Label>
            <Select value={formData.location} onValueChange={(v) => updateField('location', v)}>
              <SelectTrigger data-testid="location-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Online">Online Only</SelectItem>
                <SelectItem value="Online & In-Person">Online & In-Person</SelectItem>
                <SelectItem value="In-Person Only">In-Person Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Profile Photo URL (optional)</Label>
            <Input
              type="url"
              placeholder="https://example.com/photo.jpg"
              value={formData.profile_photo_url}
              onChange={(e) => updateField('profile_photo_url', e.target.value)}
              data-testid="photo-url-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Introduction Video URL (optional)</Label>
            <Input
              type="url"
              placeholder="https://youtube.com/watch?v=..."
              value={formData.intro_video_url}
              onChange={(e) => updateField('intro_video_url', e.target.value)}
              data-testid="video-url-input"
            />
            <p className="text-xs text-slate-500">Add a short video introducing yourself to students</p>
          </div>
        </CardContent>
      </Card>

      {/* Subjects & Grade Levels */}
      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <BookOpen className="h-5 w-5 text-indigo-700" />
            Subjects & Grade Levels
          </CardTitle>
          <CardDescription>Select all subjects and levels you can teach</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Label>Subjects You Teach *</Label>
            <div className="flex flex-wrap gap-2">
              {SUBJECTS.map(subject => (
                <Badge
                  key={subject}
                  variant={formData.subjects.includes(subject) ? "default" : "outline"}
                  className={`cursor-pointer transition-all ${
                    formData.subjects.includes(subject) 
                      ? 'bg-indigo-700 hover:bg-indigo-800' 
                      : 'hover:bg-indigo-50'
                  }`}
                  onClick={() => toggleArrayItem('subjects', subject)}
                  data-testid={`subject-${subject}`}
                >
                  {subject}
                </Badge>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <Label>Grade Levels *</Label>
            <div className="flex flex-wrap gap-2">
              {GRADE_LEVELS.map(level => (
                <Badge
                  key={level.id}
                  variant={formData.grade_levels.includes(level.id) ? "default" : "outline"}
                  className={`cursor-pointer transition-all ${
                    formData.grade_levels.includes(level.id) 
                      ? 'bg-indigo-700 hover:bg-indigo-800' 
                      : 'hover:bg-indigo-50'
                  }`}
                  onClick={() => toggleArrayItem('grade_levels', level.id)}
                  data-testid={`level-${level.id}`}
                >
                  {level.name}
                </Badge>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <Label>Languages Spoken *</Label>
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map(lang => (
                <Badge
                  key={lang}
                  variant={formData.languages.includes(lang) ? "default" : "outline"}
                  className={`cursor-pointer transition-all ${
                    formData.languages.includes(lang) 
                      ? 'bg-green-700 hover:bg-green-800' 
                      : 'hover:bg-green-50'
                  }`}
                  onClick={() => toggleArrayItem('languages', lang)}
                  data-testid={`lang-${lang}`}
                >
                  {lang}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Education & Qualifications */}
      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <Award className="h-5 w-5 text-indigo-700" />
            Education & Qualifications
          </CardTitle>
          <CardDescription>Your academic background and certifications</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Educational Background * (min 20 characters)</Label>
            <Textarea
              placeholder="List your degrees and universities attended...&#10;Example:&#10;- PhD in Mathematics, MIT (2018)&#10;- MSc in Applied Mathematics, Stanford (2014)&#10;- BSc in Mathematics, UCLA (2012)"
              value={formData.educational_background}
              onChange={(e) => updateField('educational_background', e.target.value)}
              rows={4}
              data-testid="education-textarea"
            />
            <p className="text-xs text-slate-500">{formData.educational_background.length}/20 characters minimum</p>
          </div>

          <div className="space-y-2">
            <Label>Certifications & Qualifications * (min 20 characters)</Label>
            <Textarea
              placeholder="List your teaching certifications, awards, and qualifications...&#10;Example:&#10;- State Teaching Certificate&#10;- Certified Online Educator&#10;- Published Author in Mathematics Education"
              value={formData.qualifications}
              onChange={(e) => updateField('qualifications', e.target.value)}
              rows={4}
              data-testid="qualifications-textarea"
            />
            <p className="text-xs text-slate-500">{formData.qualifications.length}/20 characters minimum</p>
          </div>
        </CardContent>
      </Card>

      {/* About & Teaching Style */}
      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <User className="h-5 w-5 text-indigo-700" />
            About You & Teaching Style
          </CardTitle>
          <CardDescription>Help students get to know you better</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Biography / About Me * (min 50 characters)</Label>
            <Textarea
              placeholder="Write a compelling biography that introduces yourself to students and parents. Share your passion for teaching, your approach, and what makes you unique..."
              value={formData.bio}
              onChange={(e) => updateField('bio', e.target.value)}
              rows={5}
              data-testid="bio-textarea"
            />
            <p className="text-xs text-slate-500">{formData.bio.length}/50 characters minimum</p>
          </div>

          <div className="space-y-2">
            <Label>Teaching Style (optional)</Label>
            <Textarea
              placeholder="Describe your teaching methodology and approach...&#10;Example:&#10;I believe in making complex concepts simple through real-world examples. My sessions are interactive and I encourage questions..."
              value={formData.teaching_style}
              onChange={(e) => updateField('teaching_style', e.target.value)}
              rows={4}
              data-testid="teaching-style-textarea"
            />
          </div>
        </CardContent>
      </Card>

      {/* Availability Schedule */}
      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <Calendar className="h-5 w-5 text-indigo-700" />
            Weekly Availability Schedule *
          </CardTitle>
          <CardDescription>Select the time slots when you're available for tutoring</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left py-2 pr-4 font-medium text-slate-700">Time</th>
                  {DAYS_OF_WEEK.map(day => (
                    <th key={day} className="text-center py-2 px-1 font-medium text-slate-700 capitalize">
                      {day.slice(0, 3)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TIME_SLOTS.map(slot => (
                  <tr key={slot} className="border-t border-stone-100">
                    <td className="py-2 pr-4 text-slate-600">{slot}</td>
                    {DAYS_OF_WEEK.map(day => {
                      const isSelected = formData.availability[day]?.includes(slot);
                      return (
                        <td key={`${day}-${slot}`} className="text-center py-1 px-1">
                          <button
                            type="button"
                            onClick={() => toggleTimeSlot(day, slot)}
                            className={`w-8 h-8 rounded transition-all ${
                              isSelected 
                                ? 'bg-indigo-700 text-white' 
                                : 'bg-stone-100 hover:bg-indigo-100 text-slate-400'
                            }`}
                            data-testid={`slot-${day}-${slot}`}
                          >
                            {isSelected && <CheckCircle className="h-4 w-4 mx-auto" />}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-4">
            Click on time slots to mark yourself as available. Selected slots will appear in indigo.
          </p>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-4 pt-4">
        <Button
          onClick={handleSubmit}
          disabled={saving}
          className="rounded-full bg-indigo-700 hover:bg-indigo-800 px-8"
          data-testid="save-profile-btn"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Profile
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

// Profile Completion Banner
const ProfileCompletionBanner = ({ profile, onEditProfile }) => {
  if (!profile) return null;
  
  const completionPercentage = profile.completion_percentage || 0;
  const isComplete = profile.profile_complete;
  const missingFields = profile.missing_fields || [];

  if (isComplete) return null;

  return (
    <Card className="border-amber-200 bg-amber-50 mb-6">
      <CardContent className="pt-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-6 w-6 text-amber-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-amber-900 mb-1">
              Complete Your Profile to Accept Bookings
            </h3>
            <p className="text-sm text-amber-700 mb-3">
              Your profile is {completionPercentage}% complete. Complete all required fields to start accepting tutoring sessions.
            </p>
            <Progress value={completionPercentage} className="h-2 mb-3" />
            {missingFields.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium text-amber-800 mb-1">Missing:</p>
                <div className="flex flex-wrap gap-1">
                  {missingFields.map(field => (
                    <Badge key={field.field} variant="outline" className="text-xs bg-white">
                      {field.label}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            <Button 
              size="sm" 
              className="rounded-full bg-amber-700 hover:bg-amber-800"
              onClick={onEditProfile}
              data-testid="complete-profile-btn"
            >
              Complete Profile Now
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const TutorDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profileRes, bookingsRes, messagesRes] = await Promise.all([
        tutorAPI.getMyProfile().catch(() => tutorAPI.get(user.user_id).catch(() => ({ data: null }))),
        bookingAPI.list(),
        messageAPI.listConversations()
      ]);
      
      setProfile(profileRes.data);
      setBookings(bookingsRes.data.bookings || []);
      setConversations(messagesRes.data.conversations || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    // Check if profile is complete before accepting bookings
    if (status === 'confirmed' && !profile?.profile_complete) {
      toast.error('Please complete your profile before accepting bookings');
      setActiveTab('profile');
      return;
    }
    
    try {
      await bookingAPI.update(bookingId, { status });
      toast.success(`Booking ${status}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const handleCreateZoomLink = async (booking) => {
    try {
      const response = await zoomAPI.createMeeting({
        booking_id: booking.booking_id,
        topic: `${booking.subject} - ${booking.student_name}`
      });
      toast.success('Zoom link created!');
      loadData();
    } catch (error) {
      toast.error('Failed to create Zoom link');
    }
  };

  const handleSaveProfile = async (profileData) => {
    setSavingProfile(true);
    try {
      await tutorAPI.updateProfile(profileData);
      toast.success('Profile updated successfully!');
      loadData();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const pendingBookings = bookings.filter(b => b.status === 'pending');
  const upcomingBookings = bookings.filter(b => 
    b.status === 'confirmed' && new Date(b.date) >= new Date()
  );
  const completedBookings = bookings.filter(b => b.status === 'completed');
  
  const totalEarnings = completedBookings.reduce((sum, b) => sum + (b.amount || 0), 0);

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    confirmed: 'bg-green-100 text-green-700',
    completed: 'bg-slate-100 text-slate-700',
    cancelled: 'bg-red-100 text-red-700'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-indigo-700" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="tutor-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-slate-900 text-white p-6 hidden lg:block">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <GraduationCap className="h-8 w-8 text-indigo-400" />
          <span className="font-heading text-lg font-semibold">Tutor Portal</span>
        </Link>

        <nav className="space-y-2">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`w-full px-4 py-2 rounded-lg flex items-center gap-3 transition-colors text-left ${
              activeTab === 'dashboard' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
            }`}
            data-testid="nav-dashboard"
          >
            <Calendar className="h-5 w-5" />
            <span>Dashboard</span>
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`w-full px-4 py-2 rounded-lg flex items-center gap-3 transition-colors text-left ${
              activeTab === 'profile' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
            }`}
            data-testid="nav-profile"
          >
            <Settings className="h-5 w-5" />
            <span>Edit Profile</span>
            {!profile?.profile_complete && (
              <Badge className="ml-auto bg-amber-600 text-white text-xs">!</Badge>
            )}
          </button>
          <Link 
            to="/messages" 
            className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white flex items-center gap-3 transition-colors"
          >
            <MessageSquare className="h-5 w-5" />
            <span>Messages</span>
            {conversations.reduce((acc, c) => acc + (c.unread_count || 0), 0) > 0 && (
              <Badge className="ml-auto bg-indigo-600 text-white">
                {conversations.reduce((acc, c) => acc + (c.unread_count || 0), 0)}
              </Badge>
            )}
          </Link>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.picture || profile?.profile_photo_url} />
              <AvatarFallback className="bg-indigo-600">{user?.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-stone-100 p-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <GraduationCap className="h-8 w-8 text-indigo-700" />
              <span className="font-heading text-lg font-semibold">Tutor Portal</span>
            </Link>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setActiveTab(activeTab === 'profile' ? 'dashboard' : 'profile')}
              >
                <Settings className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={logout}>
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>

        <div className="p-6 lg:p-8">
          {activeTab === 'dashboard' ? (
            <>
              {/* Welcome */}
              <div className="mb-6">
                <h1 className="font-heading text-3xl font-semibold text-slate-900">
                  Tutor Dashboard
                </h1>
                <p className="text-slate-600 mt-1">
                  Manage your sessions and connect with students.
                </p>
              </div>

              {/* Profile Completion Banner */}
              <ProfileCompletionBanner 
                profile={profile} 
                onEditProfile={() => setActiveTab('profile')} 
              />

              {/* Stats */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Card className="border-stone-100">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                        <Clock className="h-6 w-6 text-amber-700" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">{pendingBookings.length}</p>
                        <p className="text-sm text-slate-500">Pending</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-stone-100">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                        <Calendar className="h-6 w-6 text-indigo-700" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">{upcomingBookings.length}</p>
                        <p className="text-sm text-slate-500">Upcoming</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-stone-100">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                        <Users className="h-6 w-6 text-green-700" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">{completedBookings.length}</p>
                        <p className="text-sm text-slate-500">Completed</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-stone-100">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <DollarSign className="h-6 w-6 text-emerald-700" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">${totalEarnings.toFixed(0)}</p>
                        <p className="text-sm text-slate-500">Earnings</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Bookings */}
              <Card className="border-stone-100">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Session Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="pending">
                    <TabsList className="mb-4">
                      <TabsTrigger value="pending">Pending ({pendingBookings.length})</TabsTrigger>
                      <TabsTrigger value="upcoming">Upcoming ({upcomingBookings.length})</TabsTrigger>
                      <TabsTrigger value="completed">Completed ({completedBookings.length})</TabsTrigger>
                    </TabsList>

                    <TabsContent value="pending">
                      {pendingBookings.length === 0 ? (
                        <div className="text-center py-12">
                          <Clock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                          <h3 className="font-heading text-lg text-slate-900">No pending requests</h3>
                          <p className="text-slate-500">New booking requests will appear here.</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {pendingBookings.map((booking) => (
                            <div
                              key={booking.booking_id}
                              className="flex items-center justify-between p-4 bg-stone-50 rounded-xl"
                            >
                              <div className="flex items-center gap-4">
                                <Avatar className="h-12 w-12">
                                  <AvatarFallback className="bg-amber-100 text-amber-700">
                                    {booking.student_name?.charAt(0)}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <h4 className="font-semibold text-slate-900">{booking.student_name}</h4>
                                  <p className="text-sm text-slate-500">{booking.subject}</p>
                                  <div className="flex items-center gap-3 mt-1 text-sm text-slate-600">
                                    <span>{format(new Date(booking.date), 'MMM d, yyyy')}</span>
                                    <span>{booking.time_slot}</span>
                                    <span>${booking.amount?.toFixed(2)}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button 
                                  size="sm" 
                                  className="rounded-full bg-green-600 hover:bg-green-700 gap-1"
                                  onClick={() => handleUpdateBookingStatus(booking.booking_id, 'confirmed')}
                                  data-testid={`accept-${booking.booking_id}`}
                                >
                                  <CheckCircle className="h-4 w-4" />
                                  Accept
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="rounded-full gap-1"
                                  onClick={() => handleUpdateBookingStatus(booking.booking_id, 'cancelled')}
                                >
                                  <XCircle className="h-4 w-4" />
                                  Decline
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </TabsContent>

                    <TabsContent value="upcoming">
                      {upcomingBookings.length === 0 ? (
                        <div className="text-center py-12">
                          <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                          <h3 className="font-heading text-lg text-slate-900">No upcoming sessions</h3>
                          <p className="text-slate-500">Confirmed sessions will appear here.</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {upcomingBookings.map((booking) => (
                            <div
                              key={booking.booking_id}
                              className="flex items-center justify-between p-4 bg-stone-50 rounded-xl"
                            >
                              <div className="flex items-center gap-4">
                                <Avatar className="h-12 w-12">
                                  <AvatarFallback className="bg-indigo-100 text-indigo-700">
                                    {booking.student_name?.charAt(0)}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <h4 className="font-semibold text-slate-900">{booking.student_name}</h4>
                                  <p className="text-sm text-slate-500">{booking.subject}</p>
                                  <div className="flex items-center gap-3 mt-1 text-sm text-slate-600">
                                    <span>{format(new Date(booking.date), 'MMM d, yyyy')}</span>
                                    <span>{booking.time_slot}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {booking.zoom_link ? (
                                  <Button 
                                    size="sm" 
                                    className="rounded-full gap-2"
                                    onClick={() => window.open(booking.zoom_link, '_blank')}
                                  >
                                    <Video className="h-4 w-4" />
                                    Join
                                  </Button>
                                ) : (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="rounded-full gap-2"
                                    onClick={() => handleCreateZoomLink(booking)}
                                  >
                                    <Video className="h-4 w-4" />
                                    Create Link
                                  </Button>
                                )}
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="rounded-full"
                                  onClick={() => handleUpdateBookingStatus(booking.booking_id, 'completed')}
                                >
                                  Complete
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </TabsContent>

                    <TabsContent value="completed">
                      {completedBookings.length === 0 ? (
                        <div className="text-center py-12">
                          <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                          <h3 className="font-heading text-lg text-slate-900">No completed sessions</h3>
                          <p className="text-slate-500">Your session history will appear here.</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {completedBookings.map((booking) => (
                            <div
                              key={booking.booking_id}
                              className="flex items-center justify-between p-4 bg-stone-50 rounded-xl"
                            >
                              <div className="flex items-center gap-4">
                                <Avatar className="h-12 w-12">
                                  <AvatarFallback className="bg-slate-200 text-slate-600">
                                    {booking.student_name?.charAt(0)}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <h4 className="font-semibold text-slate-900">{booking.student_name}</h4>
                                  <p className="text-sm text-slate-500">{booking.subject}</p>
                                  <p className="text-sm text-slate-400">
                                    {format(new Date(booking.date), 'MMM d, yyyy')}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <Badge className={statusColors.completed}>Completed</Badge>
                                <p className="text-sm font-semibold text-slate-900 mt-1">
                                  ${booking.amount?.toFixed(2)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* Profile Editor Tab */}
              <div className="mb-6">
                <h1 className="font-heading text-3xl font-semibold text-slate-900">
                  Edit Your Profile
                </h1>
                <p className="text-slate-600 mt-1">
                  Complete your profile to attract more students and start accepting bookings.
                </p>
              </div>

              {/* Profile Completion Status */}
              {profile && (
                <Card className={`mb-6 ${profile.profile_complete ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        profile.profile_complete ? 'bg-green-100' : 'bg-amber-100'
                      }`}>
                        {profile.profile_complete ? (
                          <CheckCircle className="h-6 w-6 text-green-700" />
                        ) : (
                          <AlertTriangle className="h-6 w-6 text-amber-700" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-semibold ${profile.profile_complete ? 'text-green-900' : 'text-amber-900'}`}>
                          {profile.profile_complete 
                            ? 'Your profile is complete!' 
                            : `Profile ${profile.completion_percentage || 0}% complete`}
                        </h3>
                        <p className={`text-sm ${profile.profile_complete ? 'text-green-700' : 'text-amber-700'}`}>
                          {profile.profile_complete 
                            ? 'Students can now book sessions with you.' 
                            : 'Complete all required fields to accept bookings.'}
                        </p>
                        {!profile.profile_complete && (
                          <Progress value={profile.completion_percentage || 0} className="h-2 mt-2" />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <ProfileEditor 
                profile={profile} 
                onSave={handleSaveProfile}
                saving={savingProfile}
              />
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default TutorDashboard;
